// Types pour l'authentification
export interface User {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  dateOfBirth: string;
  gender: Gender;
  role: UserRole;
  relationshipType?: RelationshipType;
  memberNumber: string;
  isActive: boolean;
  profilePictureUrl?: string;
  address?: string;
  city?: string;
  postalCode?: string;
  country?: string;
  contract?: Contract;
  dependents?: User[];
  parentUserId?: string;
}

export interface UserAccount {
  id: string;
  email: string;
  phone: string;
  status: UserAccountStatus;
  preferredLoginMethod: LoginMethod;
  language: string;
  biometricEnabled: boolean;
  failedLoginAttempts: number;
  lastLoginAt?: string;
  lockedUntil?: string;
}

export enum Gender {
  MALE = 'male',
  FEMALE = 'female',
  OTHER = 'other'
}

export enum UserRole {
  ADHERENT = 'adherent',
  BENEFICIARY = 'beneficiary'
}

export enum RelationshipType {
  SELF = 'self',
  SPOUSE = 'spouse',
  CHILD = 'child',
  PARENT = 'parent',
  PARENT_USER_ID = 'parentUserId',
  OTHER = 'other'
}

// Benefits & Coverage
export enum BenefitCategoryEnum {
  CONSULTATION = 'consultation',
  PHARMACY = 'pharmacy',
  HOSPITALIZATION = 'hospitalization',
  DENTAL = 'dental',
  OPTICAL = 'optical',
  MATERNITY = 'maternity',
  OTHER = 'other'
}

export interface SimpleBenefit {
  id: string;
  category: BenefitCategoryEnum;
  name: string;
  description: string;
  coveragePercentage: number;
  annualCeiling?: number;
  waitingPeriod?: number; // months
  icon: string;
}

export interface UserBenefit extends SimpleBenefit {
  consumedAmount: number;
  remainingAmount?: number;
}

export enum UserAccountStatus {
  ACTIVE = 'active',
  INACTIVE = 'inactive',
  SUSPENDED = 'suspended',
  PENDING_ACTIVATION = 'pending_activation'
}

export enum LoginMethod {
  EMAIL_PASSWORD = 'email_password',
  OTP = 'otp',
  BIOMETRIC = 'biometric',
  PIN = 'pin'
}

// Types pour les contrats
export interface Contract {
  id: string;
  policyNumber: string;
  name: string;
  description?: string;
  startDate: string;
  endDate: string;
  status: ContractStatus;
  insurerName: string;
  insurerLogoUrl?: string;
  users: User[];
}

export enum ContractStatus {
  ACTIVE = 'active',
  INACTIVE = 'inactive',
  SUSPENDED = 'suspended',
  EXPIRED = 'expired'
}

// Types pour les prestations
export interface BenefitCategory {
  id: string;
  name: string;
  description?: string;
  icon: string;
  color: string;
  order: number;
  isActive: boolean;
  benefits: Benefit[];
}

export interface Benefit {
  id: string;
  name: string;
  description?: string;
  type: BenefitType;
  category: BenefitCategory;
  coverageRate: number;
  annualLimit?: number;
  sessionLimit?: number;
  waitingPeriod: number;
  conditions?: string;
  exclusions?: string;
  isActive: boolean;
  order: number;
}

export enum BenefitType {
  MEDICAL = 'medical',
  DENTAL = 'dental',
  OPTICAL = 'optical',
  PHARMACEUTICAL = 'pharmaceutical',
  HOSPITALIZATION = 'hospitalization',
  MATERNITY = 'maternity',
  PREVENTIVE = 'preventive'
}

// Types pour les remboursements
export interface Claim {
  id: string;
  claimNumber: string;
  user: User;
  contract: Contract;
  type: ClaimType;
  status: ClaimStatus;
  amount: number;
  approvedAmount: number;
  reimbursedAmount: number;
  providerName: string;
  treatmentDate: string;
  submissionDate?: string;
  processingDate?: string;
  reimbursementDate?: string;
  description?: string;
  notes?: string;
  rejectionReason?: string;
  documents: ClaimDocument[];
  createdAt: string;
  updatedAt: string;
  /** Référence de la facture côté gestionnaire (AG) — null si non transmise. */
  agReference?: string | null;
  /** false = soumise localement mais jamais reçue par AG ; null tant que brouillon. */
  agTransmitted?: boolean | null;
}

export interface ClaimDocument {
  id: string;
  name: string;
  type: DocumentType;
  fileUrl: string;
  fileName: string;
  fileSize: number;
  mimeType: string;
  uploadDate?: string;
  isRequired?: boolean;
}

export enum ClaimType {
  CONSULTATION = 'consultation',
  PHARMACY = 'pharmacy',
  HOSPITAL = 'hospital',
  DENTAL = 'dental',
  OPTICAL = 'optical',
  SPECIALIST = 'specialist',
  LAB = 'lab',
  OTHER = 'other'
}

export enum ClaimStatus {
  DRAFT = 'draft',
  SUBMITTED = 'submitted', // Soumise
  PENDING = 'pending',     // En cours
  APPROVED = 'approved',
  REJECTED = 'rejected',
  PAID = 'paid'
}

export enum DocumentType {
  INVOICE = 'invoice',
  PRESCRIPTION = 'prescription',
  MEDICAL_REPORT = 'medical_report',
  IDENTITY_DOCUMENT = 'identity_document',
  PAYMENT_PROOF = 'payment_proof',
  OTHER = 'other'
}

// Types pour les prestataires
export interface Provider {
  id: string;
  name: string;
  type: ProviderType;
  specialties?: string[]; // Backend treats this as string usually but kept array for now if transformed
  address: string; // Changed from Address object to string
  city: string;
  postalCode?: string;
  country?: string;
  phone?: string;
  email?: string;
  website?: string;
  description?: string;
  rating?: number; // Backend: averageRating
  reviewCount?: number; // Backend: ratingCount
  isActive: boolean;
  distance?: number;
  latitude?: number; // Flattened
  longitude?: number; // Flattened
  workingHours?: WorkingHours[]; // Backend: openingHours (json)
  acceptsThirdPartyPayment: boolean; // Backend: isPartner
  photos?: string[];
}

export interface Address {
  street: string;
  city: string;
  postalCode: string;
  country: string;
  region?: string;
}

export interface WorkingHours {
  day: string;
  openTime: string;
  closeTime: string;
  isClosed: boolean;
}

export enum ProviderType {
  DOCTOR = 'doctor',
  DENTIST = 'dentist',
  OPTICIAN = 'optician',
  PHARMACY = 'pharmacy',
  LABORATORY = 'laboratory',
  RADIOLOGY = 'radiology',
  HOSPITAL = 'hospital',
  CLINIC = 'clinic'
}

// Types pour les notifications
export interface Notification {
  id: string;
  title: string;
  message: string;
  type: NotificationType;
  priority: NotificationPriority;
  isRead: boolean;
  actionUrl?: string;
  actionLabel?: string;
  data?: Record<string, any>;
  createdAt: string;
  readAt?: string;
}

export enum NotificationType {
  INFO = 'info',
  SUCCESS = 'success',
  WARNING = 'warning',
  ERROR = 'error',
  CLAIM_UPDATE = 'claim_update',
  PAYMENT_RECEIVED = 'payment_received',
  DOCUMENT_REQUIRED = 'document_required',
  CONTRACT_EXPIRY = 'contract_expiry',
  APPOINTMENT_REMINDER = 'appointment_reminder'
}

export enum NotificationPriority {
  LOW = 'low',
  MEDIUM = 'medium',
  HIGH = 'high',
  URGENT = 'urgent'
}

// Types pour le support
export interface SupportTicket {
  id: string;
  ticketNumber: string;
  subject: string;
  description: string;
  status: TicketStatus;
  priority: TicketPriority;
  category: TicketCategory;
  user: User;
  assignedTo?: string;
  messages: TicketMessage[];
  attachments?: TicketAttachment[];
  createdAt: string;
  updatedAt: string;
  resolvedAt?: string;
}

export interface TicketMessage {
  id: string;
  message: string;
  sender: MessageSender;
  senderName: string;
  createdAt: string;
  attachments?: TicketAttachment[];
}

export interface TicketAttachment {
  id: string;
  fileName: string;
  fileUrl: string;
  fileSize: number;
  mimeType: string;
}

export enum TicketStatus {
  OPEN = 'open',
  IN_PROGRESS = 'in_progress',
  WAITING_CUSTOMER = 'waiting_customer',
  RESOLVED = 'resolved',
  CLOSED = 'closed'
}

export enum TicketPriority {
  LOW = 'low',
  MEDIUM = 'medium',
  HIGH = 'high',
  URGENT = 'urgent'
}

export enum TicketCategory {
  TECHNICAL = 'technical',
  BILLING = 'billing',
  CLAIM = 'claim',
  ACCOUNT = 'account',
  GENERAL = 'general'
}

export enum MessageSender {
  USER = 'user',
  SUPPORT = 'support',
  SYSTEM = 'system'
}

// Types pour les API responses
export interface ApiResponse<T = any> {
  success: boolean;
  data?: T;
  message?: string;
  errors?: Record<string, string[]>;
  meta?: {
    pagination?: {
      page: number;
      limit: number;
      total: number;
      totalPages: number;
    };
  };
}

export interface PaginationParams {
  page?: number;
  limit?: number;
  search?: string;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

// Types pour l'authentification API
export interface LoginRequest {
  email?: string;
  otp?: string;
  password?: string;
  loginMethod: LoginMethod;
}

export interface LoginResponse {
  tokens: {
    accessToken: string;
    refreshToken: string;
    expiresAt: string;
  };
  user: User;
}

export interface RegisterRequest {
  email: string;
  phone: string;
  password: string;
  firstName: string;
  lastName: string;
  dateOfBirth: string;
  gender: Gender;
  language?: string;
}

export interface OTPRequest {
  identifier: string;
  type: 'login' | 'registration' | 'password_reset';
}

export interface OTPVerificationRequest {
  phone: string;
  token: string;
  type: 'login' | 'registration' | 'password_reset';
}

// Types utilitaires
export interface AppError {
  message: string;
  code?: string;
  field?: string;
}

export interface LoadingState {
  isLoading: boolean;
  error?: AppError | null;
}

export interface SelectOption {
  value: string;
  label: string;
  disabled?: boolean;
}

// Types pour le router
export interface RouteParams {
  [key: string]: string | undefined;
}

// Types pour les filtres
export interface ClaimFilters {
  status?: ClaimStatus[];
  type?: ClaimType[];
  dateFrom?: string;
  dateTo?: string;
  amountMin?: number;
  amountMax?: number;
}

export interface ProviderFilters {
  type?: ProviderType[];
  specialties?: string[];
  acceptsThirdPartyPayment?: boolean;
  rating?: number;
  distance?: number;
  coordinates?: {
    latitude: number;
    longitude: number;
  };
}

export interface NotificationFilters {
  type?: NotificationType[];
  isRead?: boolean;
  priority?: NotificationPriority[];
}